import {
  GridRenderCellParams,
  GridColumns,
  GridValueFormatterParams
} from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import { baseCell } from '../cells/base-cell';
import { booleanCell } from '../cells/boolean-cell';
import {
  booleanFilterOperators,
  dateFilterOperators,
  filterOperators,
  numberFilterOperators,
  ORDER_LINE_STATUS_FILTER_OPTIONS,
  orderLineStatusColor,
  translateOrderLineStatus
} from '../../../../constants';
import { currencyFormatter } from 'helpers/currency-formatter';
import { currencyCell } from '../cells/currency-cell';
import { dateCell } from '../cells/date-cell';

export function OrderDetailColumns(): GridColumns {
  const { t } = useTranslation();
  const translationPrefix = 'entities.order_line';

  const statusOptions = ORDER_LINE_STATUS_FILTER_OPTIONS.map((option) => ({
    value: option.value,
    label: t(`entities.order_line.${option.i18nKey}`)
  }));

  const columns: GridColumns = [
    {
      field: 'product',
      headerName: t(`global.product`),
      width: 150,
      filterOperators,
      valueGetter: (params) => params.row.product.code,
      renderCell: baseCell
    },
    {
      field: 'product_description',
      headerName: t(`global.product_description`),
      width: 250,
      filterOperators,
      valueGetter: (params) => params.row.product.description,
      renderCell: baseCell
    },
    {
      field: 'supplier',
      headerName: t(`global.supplier`),
      width: 250,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'is_periodic',
      headerName: t('entities.order_line.is_periodic'),
      type: 'boolean',
      width: 150,
      filterable: true,
      filterOperators: booleanFilterOperators,
      sortable: true,
      renderCell: booleanCell(t)
    },
    {
      field: 'warehouse',
      headerName: t('global.warehouse'),
      width: 200,
      filterable: true,
      filterOperators,
      sortable: true,
      renderCell: baseCell
    },
    {
      field: 'ref',
      headerName: t(`entities.order_line.ref`),
      width: 100,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'order_date',
      headerName: t(`entities.movement.order_date`),
      width: 100,
      filterOperators: dateFilterOperators,
      renderCell: baseCell
    },
    {
      field: 'requested_shipment_date',
      type: 'date',
      headerName: t(`entities.order_line.requested_shipment_date`),
      width: 150,
      renderCell: dateCell
    },

    {
      field: 'remaining_quantity',
      type: 'number',
      headerName: t('entities.order_line.remaining_quantity'),
      width: 150,
      filterOperators: numberFilterOperators,
      sortable: true,
      renderCell: baseCell
    },
    {
      field: 'status',
      headerName: t('global.status'),
      width: 100,
      type: 'singleSelect',
      valueOptions: statusOptions.map((option) => ({
        value: option.value,
        label: option.label
      })),
      sortable: true,
      valueFormatter: (params: GridValueFormatterParams) => {
        return translateOrderLineStatus(t, params.value);
      },
      renderCell: (params: GridRenderCellParams) => {
        return (
          <div>
            <span className={orderLineStatusColor(params.value)}>
              {translateOrderLineStatus(t, params.value)}
            </span>
          </div>
        );
      }
    },
    {
      field: 'notes',
      headerName: t('global.notes'),
      width: 300,
      filterOperators,
      renderCell: baseCell
    }
  ];

  ['unit_price', 'pending_amount', 'amount'].forEach((field) => {
    columns.push({
      field: field,
      width: 150,
      headerName: t(`${translationPrefix}.${field}`),
      type: 'number',
      editable: false,
      filterable: true,
      filterOperators: numberFilterOperators,
      sortable: true,
      valueFormatter: (params: GridValueFormatterParams) => {
        return currencyFormatter(params.value, { significantDigits: 3 });
      },
      renderCell: (params: GridRenderCellParams) => currencyCell(params, 3)
    });
  });

  return columns;
}
